import { useEffect, useState, useCallback } from 'react';
import { listAllFormStates, loadUserFormState, adminRestoreFormState, exportFormState, deleteFormStateFromS3, listActiveUsers, adminPushToSession } from '../services/formStateS3Service';
import UserStatusBadge from './UserStatusBadge';

const AdminModal = ({ isOpen, onClose, currentUser, getCurrentFormSnapshot }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'savedAt', direction: 'desc' });
  const [userStatusOpen, setUserStatusOpen] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [selectedTargetUser, setSelectedTargetUser] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);

  // Define callbacks BEFORE effects to avoid TDZ errors when referenced in effects
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAllFormStates(currentUser);
      setForms(res.forms || []);
    } catch (err) {
      console.error('Admin: failed to list forms', err);
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadActiveUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listActiveUsers(currentUser);
      setActiveUsers(res.users || []);
      setUserStatusOpen(true);
    } catch (err) {
      console.error('Admin: failed to list active users', err);
      setError(err.message || 'Failed to load active users');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      loadAll();
      // Also pre-load active users to allow push-to-live UI
      (async () => {
        try {
          const res = await listActiveUsers(currentUser);
          setActiveUsers(res.users || []);
        } catch (e) {
          console.warn('Could not pre-load active users', e);
        }
      })();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, currentUser, loadAll]);

  // Lightweight polling: refresh active users every 8 seconds while the modal is open
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    const fetchActive = async () => {
      try {
        const res = await listActiveUsers(currentUser);
        if (mounted) setActiveUsers(res.users || []);
      } catch (e) {
        // silent - polling should be non-intrusive
        console.debug('AdminModal: polling error', e?.message || e);
      }
    };
    // initial fetch already done; start interval
    const iv = setInterval(fetchActive, 8000);
    return () => { mounted = false; clearInterval(iv); };
  }, [isOpen, currentUser]);

  const formatTimeAgo = (iso) => {
    if (!iso) return '';
    try {
      const then = new Date(iso);
      const diff = Date.now() - then.getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins === 1) return '1 min ago';
      if (mins < 60) return `${mins} mins ago`;
      const hours = Math.floor(mins / 60);
      if (hours === 1) return '1 hour ago';
      if (hours < 24) return `${hours} hours ago`;
      const days = Math.floor(hours / 24);
      return `${days} day${days>1?'s':''} ago`;
    } catch (e) {
      return '';
    }
  };

  const openConfirm = (message, cb) => {
    setConfirmMessage(message);
    setConfirmCallback(() => cb);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmCallback(null);
  };

  const handleRestoreToUser = async (form) => {
    // form should contain owner or username field
    const targetUser = form.username || form.owner || (form.formState && form.formState.savedBy);
    const referenceNo = form.referenceNo || (form.formState && form.formState.referenceNo);
    if (!targetUser || !referenceNo) {
      alert('Cannot determine target user or reference number for this saved form');
      return;
    }

    if (!window.confirm(`Restore saved state ${referenceNo} back to ${targetUser}? This will overwrite their saved state for the same reference number.`)) return;

    try {
      setLoading(true);
      // Load the full form state first (admin load) if available on the object
      let state = form.formState || null;
      if (!state) {
        const loaded = await loadUserFormState(targetUser, referenceNo);
        state = loaded.formState;
      }

      // If the target user is currently active, try to push to their live session first
      const isActive = activeUsers.some(u => u.username === targetUser && (u.status === 'active' || u.state === 'active'));
      if (isActive && typeof adminPushToSession === 'function') {
        try {
          // Attempt live push
          const pushRes = await adminPushToSession(currentUser, targetUser, referenceNo, state);
          alert('Pushed to live session: ' + (pushRes.message || 'OK'));
        } catch (pushErr) {
          console.warn('Live push failed, falling back to restore:', pushErr);
          const res = await adminRestoreFormState(targetUser, referenceNo, state);
          alert('Restore result: ' + (res.message || 'OK'));
        }
      } else {
        const res = await adminRestoreFormState(targetUser, referenceNo, state);
        alert('Restore result: ' + (res.message || 'OK'));
      }

      // reload list
      await loadAll();
    } catch (err) {
      console.error('Admin restore failed', err);
      alert('Restore failed: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Push to live explicitly (separate action from restore)
  const handlePushToLive = async (form) => {
    const targetUser = form.username || form.owner || (form.formState && form.formState.savedBy);
    const referenceNo = form.referenceNo || (form.formState && form.formState.referenceNo);
    if (!targetUser || !referenceNo) {
      alert('Cannot determine target user or reference number for this saved form');
      return;
    }

    if (!window.confirm(`Push saved state ${referenceNo} into ${targetUser}'s live session?`)) return;

    try {
      setLoading(true);
      let state = form.formState || null;
      if (!state) {
        const loaded = await loadUserFormState(targetUser, referenceNo);
        state = loaded.formState;
      }

      const res = await adminPushToSession(currentUser, targetUser, referenceNo, state);
      alert('Push result: ' + (res.message || 'OK'));
    } catch (err) {
      console.error('Push to live failed', err);
      alert('Push failed: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Push the selected saved form into the current admin's live session
  const handlePushSavedToMe = async (form) => {
    if (!currentUser) return alert('Current user not available for push-to-me');
    const referenceNo = form.referenceNo || (form.formState && form.formState.referenceNo);
    if (!referenceNo) return alert('Reference number required for push');
    if (!window.confirm(`Push saved state ${referenceNo} into your live session (${currentUser})? This will overwrite your live instance.`)) return;
    try {
      setLoading(true);
      let state = form.formState || null;
      if (!state) {
        const loaded = await loadUserFormState(form.username || currentUser, referenceNo);
        state = loaded.formState;
      }
      const res = await adminPushToSession(currentUser, currentUser, referenceNo, state);
      alert('Push to me result: ' + (res.message || 'OK'));
    } catch (err) {
      console.error('Push to me failed', err);
      alert('Push to me failed: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Push the admin's current live snapshot into a chosen user's live session
  const handlePushMyCurrentToUser = async (form) => {
    if (!getCurrentFormSnapshot || typeof getCurrentFormSnapshot !== 'function') return alert('Current form snapshot not available');
    const target = selectedTargetUser || (form.username || form.owner);
    if (!target) return alert('Please select a target user from the Active Users dropdown');
    const referenceNo = form.referenceNo || (form.formState && form.formState.referenceNo) || getCurrentFormSnapshot().referenceNo;
    if (!referenceNo) return alert('Reference number required to push');

    openConfirm(`Push your current live state (ref ${referenceNo}) into ${target}'s live session? This will attempt to overwrite their live instance.`, async () => {
      try {
        setLoading(true);
        const snapshot = getCurrentFormSnapshot();
        const res = await adminPushToSession(currentUser, target, referenceNo, snapshot);
        alert('Push my current state result: ' + (res.message || 'OK'));
      } catch (err) {
        console.error('Push my current state failed', err);
        alert('Push failed: ' + (err.message || err));
      } finally {
        setLoading(false);
        closeConfirm();
      }
    });
  };

  const handleExport = (form, type) => {
    try {
      const state = form.formState || {};
      const content = exportFormState(state, type);
      const blob = new Blob([content], { type: type === 'csv' ? 'text/csv' : 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ref = form.referenceNo || (state && state.referenceNo) || 'formstate';
      a.download = `${ref}.${type === 'csv' ? 'csv' : 'txt'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
      alert('Export failed: ' + (err.message || err));
    }
  };

  const handleDelete = async (form, e) => {
    e?.stopPropagation();
    const username = form.username || form.owner || (form.formState && form.formState.savedBy);
    const referenceNo = form.referenceNo || (form.formState && form.formState.referenceNo);
    if (!username || !referenceNo) {
      alert('Cannot determine username/referenceNo for delete');
      return;
    }
    if (!window.confirm(`Delete saved state ${referenceNo} for user ${username}?`)) return;
    try {
      setLoading(true);
      await deleteFormStateFromS3(username, referenceNo);
      await loadAll();
      alert('Deleted');
    } catch (err) {
      console.error('Delete failed', err);
      alert('Delete failed: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sorted = [...forms].sort((a,b) => {
    const aVal = ((a[sortConfig.key] ?? (a.formState && a.formState[sortConfig.key])) || '').toString();
    const bVal = ((b[sortConfig.key] ?? (b.formState && b.formState[sortConfig.key])) || '').toString();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredSorted = filterUser ? sorted.filter(f => {
    const username = f.username || (f.formState && f.formState.savedBy) || f.owner || '';
    return username === filterUser;
  }) : sorted;

  if (!isOpen) return null;

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:'20px'}}>
      <div style={{backgroundColor:'white',borderRadius:8,maxWidth:'1100px',width:'100%',maxHeight:'85vh',display:'flex',flexDirection:'column'}}>
        <div style={{padding:20,borderBottom:'1px solid #e5e7eb',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h2 style={{margin:0}}>User Form States (Admin)</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.5rem',cursor:'pointer'}}>×</button>
        </div>
        <div style={{padding:20,overflowY:'auto',flex:1}}>
          <div style={{marginBottom:12, display:'flex', gap:8, alignItems:'center'}}>
            <button onClick={() => loadActiveUsers()} disabled={loading} style={{padding:'6px 12px', borderRadius:4, background:'#111827', color:'white', border:'none'}}>Refresh Users</button>
            <select value={selectedTargetUser} onChange={(e) => setSelectedTargetUser(e.target.value)} style={{padding:'6px 10px', borderRadius:4, border:'1px solid #d1d5db'}}>
              <option value="">Push target (active users)</option>
              {activeUsers.map(u => (
                <option key={u.username} value={u.username}>{u.username} {u.status ? `(${u.status})` : ''}</option>
              ))}
            </select>
            <button onClick={() => { if (selectedTargetUser) { setFilterUser(selectedTargetUser); } }} disabled={!selectedTargetUser} style={{padding:'6px 10px', borderRadius:4, border:'1px solid #d1d5db'}}>Show Saved States for Selected</button>
            <button onClick={async () => {
                if (!selectedTargetUser) return alert('Select a target user first');
                if (!getCurrentFormSnapshot) return alert('No current snapshot available');
                if (!window.confirm(`Push your current live state into ${selectedTargetUser}'s live session? This will attempt to overwrite their live instance.`)) return;
                try {
                  setLoading(true);
                  const snapshot = getCurrentFormSnapshot();
                  await adminPushToSession(currentUser, selectedTargetUser, snapshot.referenceNo || snapshot.referenceNo, snapshot);
                  alert('Push sent (attempted)');
                } catch (err) {
                  console.error('Push failed', err);
                  alert('Push failed: ' + (err.message || err));
                } finally { setLoading(false); }
              }} disabled={!getCurrentFormSnapshot || !selectedTargetUser} style={{padding:'6px 10px', borderRadius:4, background:'#0ea5a4', color:'white', border:'none'}}>Push My Current → Selected</button>
            <div style={{color:'#6b7280', fontSize:13}}>{selectedTargetUser ? `Selected: ${selectedTargetUser}` : 'No target selected'}</div>
          </div>
          {error && <div style={{background:'#fee2e2',padding:12,borderRadius:4,color:'#991b1b'}}>{error}</div>}
          {loading && <div style={{padding:40,textAlign:'center'}}>Loading...</div>}
          {!loading && sorted.length === 0 && <div style={{padding:40,textAlign:'center'}}>No saved states found</div>}
          {!loading && sorted.length > 0 && (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#f9fafb',borderBottom:'2px solid #e5e7eb'}}>
                    <th onClick={() => handleSort('savedAt')} style={{padding:12,textAlign:'left',cursor:'pointer'}}>Saved (AEST)</th>
                    <th onClick={() => handleSort('referenceNo')} style={{padding:12,textAlign:'left',cursor:'pointer'}}>Reference</th>
                    <th onClick={() => handleSort('username')} style={{padding:12,textAlign:'left',cursor:'pointer'}}>Username</th>
                    <th onClick={() => handleSort('eventName')} style={{padding:12,textAlign:'left',cursor:'pointer'}}>Event Name</th>
                    <th onClick={() => handleSort('sectionName')} style={{padding:12,textAlign:'left',cursor:'pointer'}}>Section</th>
                    <th style={{padding:12,textAlign:'center',width:220}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((f, idx) => (
                    <tr key={(f.referenceNo||'')+idx} style={{background: idx%2===0?'white':'#f9fafb',borderBottom:'1px solid #e5e7eb'}}>
                      <td style={{padding:12}}>{new Date((f.savedAt || (f.formState && f.formState.savedAt) || '')).toLocaleString('en-AU', {timeZone:'Australia/Sydney'})}</td>
                      <td style={{padding:12,fontWeight:500}}>{f.referenceNo || (f.formState && f.formState.referenceNo)}</td>
                      <td style={{padding:12}}>{f.username || (f.formState && f.formState.savedBy) || f.owner || ''}</td>
                      <td style={{padding:12}}>{f.eventName || (f.formState && f.formState.eventData && f.formState.eventData.eventName) || ''}</td>
                      <td style={{padding:12}}>{f.sectionName || (f.formState && f.formState.sectionName) || ''}</td>
                      <td style={{padding:12,textAlign:'center'}}>
                        {(() => {
                          const targetUser = f.username || (f.formState && f.formState.savedBy) || f.owner;
                          const isActiveRow = targetUser && activeUsers.some(u => u.username === targetUser && (u.status === 'active' || u.state === 'active'));
                          return (
                            <>
                              {isActiveRow && (
                                <button onClick={() => handlePushToLive(f)} style={{marginRight:8,background:'#f59e0b',color:'white',border:'none',padding:'6px 10px',borderRadius:4}}>Push Live</button>
                              )}
                              {/* Push saved form into current admin's live session */}
                              {currentUser && (
                                <button onClick={() => handlePushSavedToMe(f)} style={{marginRight:8,background:'#2563eb',color:'white',border:'none',padding:'6px 10px',borderRadius:4}}>Push To Me</button>
                              )}
                              <button onClick={() => handleRestoreToUser(f)} style={{marginRight:8}}>Restore</button>
                              {/* Push admin's current live state into another user's session */}
                              {currentUser && getCurrentFormSnapshot && (
                                <button onClick={() => handlePushMyCurrentToUser(f)} style={{marginRight:8,background:'#0ea5a4',color:'white',border:'none',padding:'6px 10px',borderRadius:4}}>Push My Current → User</button>
                              )}
                            </>
                          );
                        })()}
                        <button onClick={() => handleExport(f, 'txt')} style={{marginRight:8}}>Export TXT</button>
                        <button onClick={() => handleExport(f, 'csv')} style={{marginRight:8}}>Export CSV</button>
                        <button onClick={(e) => handleDelete(f, e)} style={{marginLeft:8,color:'#ef4444'}}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* All Users with Saved Forms */}
          {forms.length > 0 && !loading && (
            <div style={{marginTop:20,padding:15,backgroundColor:'#eff6ff',borderRadius:8,border:'1px solid #bfdbfe'}}>
              <h3 style={{margin:'0 0 12px 0',fontSize:16,fontWeight:600,color:'#1e40af'}}>
                📋 All Users with Saved Forms ({[...new Set(forms.map(f => f.username))].length} users, {forms.length} forms)
              </h3>
              <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                {[...new Set(forms.map(f => f.username))].sort().map((user) => {
                  const userFormCount = forms.filter(f => f.username === user).length;
                  const isActive = activeUsers.some(u => u.username === user);
                  const isFiltered = filterUser === user;
                  return (
                    <div 
                      key={user}
                      style={{
                        display:'inline-flex',
                        alignItems:'center',
                        gap:8,
                        padding:'8px 14px',
                        backgroundColor: isFiltered ? '#3b82f6' : 'white',
                        color: isFiltered ? 'white' : '#111827',
                        border: isFiltered ? '2px solid #2563eb' : '1px solid #d1d5db',
                        borderRadius:8,
                        fontSize:13,
                        cursor:'pointer',
                        transition:'all 0.2s',
                        fontWeight: isFiltered ? 600 : 400
                      }}
                      onClick={() => setFilterUser(isFiltered ? '' : user)}
                      onMouseEnter={e => {
                        if (!isFiltered) {
                          e.currentTarget.style.backgroundColor = '#dbeafe';
                          e.currentTarget.style.borderColor = '#3b82f6';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isFiltered) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                      title={`Click to ${isFiltered ? 'clear filter' : 'filter by'} ${user}`}
                    >
                      {isActive && <span style={{fontSize:12}}>🟢</span>}
                      <span style={{fontWeight:600,textTransform:'capitalize'}}>{user}</span>
                      <span style={{
                        fontSize:11,
                        padding:'3px 8px',
                        backgroundColor: isFiltered ? '#1e40af' : (isActive ? '#10b981' : '#6b7280'),
                        color:'white',
                        borderRadius:12,
                        fontWeight:700,
                        minWidth:20,
                        textAlign:'center'
                      }}>
                        {userFormCount}
                      </span>
                    </div>
                  );
                })}
              </div>
              {filterUser && (
                <div style={{marginTop:12,fontSize:13,color:'#1e40af',fontWeight:500}}>
                  ✓ Filtering by: <strong>{filterUser}</strong> ({forms.filter(f => f.username === filterUser).length} forms)
                </div>
              )}
            </div>
          )}

          {userStatusOpen && (
            <div style={{marginTop:16,padding:12,border:'1px solid #e5e7eb',borderRadius:6}}> 
              <h3 style={{marginTop:0}}>Active Users</h3>
              {activeUsers.length === 0 ? (
                <div>No active users found</div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'#f9fafb'}}><th style={{padding:8}}>Username</th><th style={{padding:8}}>Status</th><th style={{padding:8}}>Last Active</th></tr>
                  </thead>
                  <tbody>
                    {activeUsers.map((u, i) => (
                      <tr key={u.username + i} style={{borderBottom:'1px solid #e5e7eb', cursor:'pointer'}} onClick={() => { setSelectedTargetUser(u.username); setFilterUser(u.username); }} title={`Click to select ${u.username}`}>
                        <td style={{padding:8}}>{u.username}</td>
                        <td style={{padding:8}}>
                          <div style={{display:'flex', alignItems:'center', gap:8}}>
                            <UserStatusBadge status={u.status || u.state || 'unknown'} size="sm" />
                            <span style={{textTransform:'capitalize'}}>{u.status || u.state || 'unknown'}</span>
                          </div>
                        </td>
                        <td style={{padding:8}}>{u.lastActive ? `${formatTimeAgo(u.lastActive)} (${new Date(u.lastActive).toLocaleString('en-AU', {timeZone:'Australia/Sydney'})})` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {/* Confirmation modal (simple inline) */}
          {confirmOpen && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,display:'flex',alignItems:'center',justifyContent:'center',zIndex:10001}}>
              <div style={{background:'rgba(0,0,0,0.5)',position:'absolute',inset:0}} onClick={closeConfirm} />
              <div style={{background:'white',padding:20,borderRadius:8,zIndex:10002,width:520,maxWidth:'95%'}}>
                <div style={{marginBottom:12}}>{confirmMessage}</div>
                <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                  <button onClick={() => { closeConfirm(); }} style={{padding:'8px 12px'}}>Cancel</button>
                  <button onClick={async () => { if (confirmCallback) await confirmCallback(); }} style={{padding:'8px 12px',background:'#111827',color:'white',border:'none',borderRadius:4}}>Confirm</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div style={{padding:16,borderTop:'1px solid #e5e7eb',display:'flex',justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'8px 16px'}}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AdminModal;
